import React, { useState, useEffect, useContext, useRef } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { useFdChatUrl } from 'hooks/useFdChatUrl'
import { AppState, AppStateContext } from 'containers/State/AppState'
import useStyles from './FdChat.styles'

export const FdChat: React.FC = () => {
  const [fdchatLoaded, setFdchatLoaded] = useState(false)
  const { classes, cx: classNames } = useStyles()
  const { url: fdchatUrl, shouldRedirectToParticipantExperience } =
    useFdChatUrl()
  const {
    responseChoicePromises,
    respondentProgress: [progress, setProgress]
  } = useContext<AppState>(AppStateContext)
  const redirectStartedRef = useRef(false)

  useEffect(() => {
    if (!shouldRedirectToParticipantExperience) return
    if (redirectStartedRef.current) return
    redirectStartedRef.current = true

    const awaitPromisesAndRedirect = async () => {
      if (responseChoicePromises.length > 0) {
        datadogLogs.logger.info(
          'FdChat: Waiting for response choices before redirect',
          { pendingCount: responseChoicePromises.length }
        )

        const results = await Promise.allSettled(responseChoicePromises)
        const failedCount = results.filter(
          (r) => r.status === 'rejected'
        ).length

        datadogLogs.logger.info('FdChat: Response choices settled', {
          total: results.length,
          failed: failedCount,
          succeeded: results.length - failedCount
        })

        if (failedCount > 0) {
          datadogLogs.logger.warn(
            'FdChat: Response choices failed, quality terminating respondent',
            { failedCount, totalCount: results.length }
          )

          if (progress) {
            setProgress({
              ...progress,
              isQualityTerminated: true,
              isCompleted: false
            })
          }
          return
        }
      }

      window.location.assign(fdchatUrl)
    }

    awaitPromisesAndRedirect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fdchatUrl, shouldRedirectToParticipantExperience])

  if (shouldRedirectToParticipantExperience) {
    return (
      <div
        data-testid="spinner"
        className={classNames('cover', classes.spinnerWrapper)}
      >
        <div className="spinner">
          <div className="static" />
        </div>
      </div>
    )
  }

  return (
    <>
      <div
        data-testid="spinner"
        className={classNames('cover', classes.spinnerWrapper, {
          fdchatLoading: !fdchatLoaded
        })}
      >
        <div className="spinner">
          <div className="static" />
        </div>
      </div>
      <iframe
        data-testid="iframe"
        title="fdchat"
        onLoad={() => setFdchatLoaded(true)}
        height="100%"
        width="100%"
        className={classNames(classes.iframe, { fdchatLoading: !fdchatLoaded })}
        src={fdchatUrl}
      />
    </>
  )
}
